import { useState, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { tradingService } from '../../../services/TradingService';
import { StrategyPositionSnapshot } from '../../../types/trading';
import { BacktestRequest } from '../../order-ticket/strategy-backtest/config/types';
import { errorMessage, removeStrategyPosition, toBacktestRequest } from './formatters';

export interface UseStrategyActionsResult {
  expandedStrategies: Record<string, boolean>;
  toggleStrategyExpanded: (id: string) => void;
  strategyCloseTarget: StrategyPositionSnapshot | null;
  strategyCloseSubmitting: boolean;
  strategyCloseError: string | null;
  openStrategyCloseModal: (sp: StrategyPositionSnapshot) => void;
  dismissStrategyCloseModal: () => void;
  handleConfirmStrategyClose: () => Promise<void>;
  strategyViewTarget: StrategyPositionSnapshot | null;
  strategyViewConfig: BacktestRequest | null;
  openStrategyViewModal: (sp: StrategyPositionSnapshot) => void;
  closeStrategyViewModal: () => void;
}

export function useStrategyActions(
  setStrategyPositions: React.Dispatch<React.SetStateAction<StrategyPositionSnapshot[]>>,
): UseStrategyActionsResult {
  const { userUuid, getToken } = useAuth();

  const [expandedStrategies, setExpandedStrategies] = useState<Record<string, boolean>>({});
  const [strategyCloseTarget, setStrategyCloseTarget] = useState<StrategyPositionSnapshot | null>(null);
  const [strategyCloseSubmitting, setStrategyCloseSubmitting] = useState(false);
  const [strategyCloseError, setStrategyCloseError] = useState<string | null>(null);
  const [strategyViewTarget, setStrategyViewTarget] = useState<StrategyPositionSnapshot | null>(null);

  const toggleStrategyExpanded = useCallback(
    (id: string) => setExpandedStrategies((p) => ({ ...p, [id]: !p[id] })),
    [],
  );

  const openStrategyCloseModal = useCallback(
    (sp: StrategyPositionSnapshot) => { setStrategyCloseError(null); setStrategyCloseTarget(sp); },
    [],
  );

  const dismissStrategyCloseModal = useCallback(() => {
    if (strategyCloseSubmitting) return;
    setStrategyCloseError(null);
    setStrategyCloseTarget(null);
  }, [strategyCloseSubmitting]);

  const handleConfirmStrategyClose = useCallback(async () => {
    if (!strategyCloseTarget || !userUuid) return;
    setStrategyCloseSubmitting(true);
    setStrategyCloseError(null);
    try {
      const token = await getToken();
      await tradingService.closeStrategyPosition(userUuid, strategyCloseTarget.strategy_position_id, token || undefined);
      setStrategyPositions((prev) => removeStrategyPosition(prev, strategyCloseTarget.strategy_position_id));
      setExpandedStrategies((prev) => { const n = { ...prev }; delete n[strategyCloseTarget.strategy_position_id]; return n; });
      setStrategyViewTarget((prev) => (prev?.strategy_position_id === strategyCloseTarget.strategy_position_id ? null : prev));
      setStrategyCloseTarget(null);
    } catch (err) {
      setStrategyCloseError(errorMessage(err, 'Failed to close strategy position'));
    } finally {
      setStrategyCloseSubmitting(false);
    }
  }, [getToken, strategyCloseTarget, userUuid, setStrategyPositions]);

  const openStrategyViewModal = useCallback((sp: StrategyPositionSnapshot) => setStrategyViewTarget(sp), []);
  const closeStrategyViewModal = useCallback(() => setStrategyViewTarget(null), []);
  const strategyViewConfig = useMemo(() => toBacktestRequest<BacktestRequest>(strategyViewTarget?.config), [strategyViewTarget]);

  /* ── Escape key for modals ───────────────────────────────────────── */

  useEffect(() => {
    if (!strategyCloseTarget && !strategyViewTarget) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (strategyViewTarget) { closeStrategyViewModal(); return; }
      dismissStrategyCloseModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeStrategyViewModal, dismissStrategyCloseModal, strategyCloseTarget, strategyViewTarget]);

  return {
    expandedStrategies,
    toggleStrategyExpanded,
    strategyCloseTarget,
    strategyCloseSubmitting,
    strategyCloseError,
    openStrategyCloseModal,
    dismissStrategyCloseModal,
    handleConfirmStrategyClose,
    strategyViewTarget,
    strategyViewConfig,
    openStrategyViewModal,
    closeStrategyViewModal,
  };
}
